import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createBlock as _createBlock, Teleport as _Teleport, createVNode as _createVNode, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "segment scheme-light content faq-list" }
const _hoisted_2 = { class: "container-content" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-10-10 col-10-md-2" }
const _hoisted_5 = { class: "facets" }
const _hoisted_6 = { class: "facet-reset" }
const _hoisted_7 = { class: "col-10-10 col-10-md-8" }
const _hoisted_8 = ["innerHTML"]

import { inject, ref } from 'vue';
import FacetsContainer from './components/FacetsContainer.vue';
import axios, { AxiosResponse } from 'axios';
import {
    RequestUrl,
    parseSolrResponse,
    ParsedSolrResponseData,
    SolrArguments,
    SolrResponseData
} from '../modules/solr'; // eslint-disable-line import/no-relative-parent-imports
import Accordion, { AccordionTabOpenEvent } from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';


export default /*#__PURE__*/_defineComponent({
  __name: 'FaqList',
  props: {
    endpoint: {},
    pluginNamespace: {},
    arguments: {}
  },
  setup(__props: any) {

const props = __props;



const i18n = inject('i18n') as { [key: string]: string };

const defaultArguments: SolrArguments = {
    filter: ['type:tx_fwf_faq_item'], // fallback
    page: '1', // required
    perPage: '10000', // fallback: all
    q: '*', // required
    sort: 'sorting asc' // fallback
};

const userFacetFields = ['faqCategory']; // array order defines display order
const baseArguments: SolrArguments = { ...defaultArguments, ...JSON.parse(props.arguments) as SolrArguments };
const baseUrl = RequestUrl.buildFromArguments(props.endpoint, props.pluginNamespace, baseArguments);
const initialUrl = buildRequestUrlWithAdditionalArgumentsFromLocationUrlHash(baseUrl, window.location.hash);
let currentUrl = new RequestUrl(initialUrl);
const pageUrl = inject('pageUrl') as string;
const activeAccordionIndex = ref(-1); // No open items
const componentKey = ref(0);

const currentSolrResponse = ref<ParsedSolrResponseData>({
    facets: [],
    documents: [],
    currentPageNumber: 1,
    totalNumberOfPages: 1
});

function buildRequestUrlWithAdditionalArgumentsFromLocationUrlHash (url: RequestUrl, hash: string): RequestUrl {
    // Add FAQ category based on hash
    if (hash.charAt(1) === '/') {
        url.addFilterParameter('faqCategory:' + decodeURI(hash.substring(1)));
    }
    return new RequestUrl(baseUrl);
}

function accordionTabOpenEvent (event: AccordionTabOpenEvent) {
    history.replaceState(null, '', currentSolrResponse.value.documents[event.index].url.toString());
}

function accordionTabCloseEvent () {
    // do not remove hash for category
    if (window.location.hash && window.location.hash.charAt(1) !== '/') {
        history.replaceState(null, '', window.location.pathname);
    }
}

function loadFaqsAfterFacetChange (name: string, value: string, type: string): void {
    const url = new RequestUrl(currentUrl);
    switch (type) {
        case 'add':
            url.addFilterParameter(`${name}:${value}`);
            if (name === 'faqCategory') {
                history.replaceState(null, '', '#' + encodeURI(value));
            }
            break;
        case 'set':
            url.removeFilter(name);
            url.addFilterParameter(`${name}:${value}`);
            if (name === 'faqCategory') {
                history.replaceState(null, '', '#' + encodeURI(value));
            }
            break;
        default:
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            console.trace(`Invalid change type '${type}' (facet change).`);
    }
    url.setPage(1);
    loadFaqsAfterFacetChanged(url.toString());

    currentUrl = url;
}

function loadFaqsAfterFacetChanged (url: string) {
    axios.get(url).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        activeAccordionIndex.value = -1;
        // We do this here to force the accordion component to update
        // https://github.com/primefaces/primevue/issues/4661
        componentKey.value += 1;
        return undefined;
    }).catch(() => console.trace('Loading of faqs failed (facet change).'));
}

function resetFilter (): void {
    loadInitialList().catch(() => console.trace('Loading of faqs failed (filter reset).'));
}

function loadInitialList (): Promise<void | undefined> {
    return axios.get(initialUrl.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        return undefined;
    });
}

loadInitialList().then(() => {
    // open tab for FAQ item based on hash
    if (window.location.hash && window.location.hash.charAt(1) !== '/') {
        for (const [index, document] of currentSolrResponse.value.documents.entries()) {
            const [, slug] = document.url.split('#');
            if (slug === window.location.hash.substring(1)) {
                activeAccordionIndex.value = index;
                break;
            }
        }
    }
}).catch(() => console.trace('Initial loading of faqs failed.'));


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"FAQPage\", \"url\": \"" + _toDisplayString(_unref(pageUrl)) + "\", \"mainEntity\": [ ", 1 /* TEXT */),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentSolrResponse.value.documents, (document, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
              _createTextVNode(" { \"@type\": \"Question\", \"name\": \"" + _toDisplayString(document.title) + "\", \"acceptedAnswer\": { \"@type\": \"Answer\", \"text\": \"" + _toDisplayString(document.content) + "\" } } " + _toDisplayString(index != currentSolrResponse.value.documents.length - 1 ? ',' : ''), 1 /* TEXT */)
            ], 64 /* STABLE_FRAGMENT */))
          }), 256 /* UNKEYED_FRAGMENT */)),
          _createTextVNode(" ] } ")
        ]),
        _: 1 /* STABLE */
      }))
    ])),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(FacetsContainer, {
                facets: currentSolrResponse.value.facets,
                onChange: loadFaqsAfterFacetChange
              }, null, 8 /* PROPS */, ["facets"])
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("button", {
                class: "button button-reset",
                onClick: resetFilter
              }, _toDisplayString(_unref(i18n).resetFilter), 1 /* TEXT */)
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            (_openBlock(), _createBlock(_unref(Accordion), {
              key: componentKey.value,
              activeIndex: activeAccordionIndex.value,
              "onUpdate:activeIndex": _cache[0] || (_cache[0] = ($event: any) => ((activeAccordionIndex).value = $event)),
              "expand-icon": "icon icon-arrow-down-dark",
              "collapse-icon": "icon icon-arrow-up-dark",
              onTabOpen: accordionTabOpenEvent,
              onTabClose: accordionTabCloseEvent
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentSolrResponse.value.documents, (document) => {
                  return (_openBlock(), _createBlock(_unref(AccordionTab), {
                    key: document.id,
                    header: document.title,
                    pt: {
                                header: {
                                    'role': 'heading',
                                    'aria-level': '3'
                                }
                            }
                  }, {
                    default: _withCtx(() => [
                      _createCommentVNode(" eslint-disable vue/no-v-html "),
                      _createElementVNode("div", {
                        innerHTML: document.contentHtml
                      }, null, 8 /* PROPS */, _hoisted_8)
                    ]),
                    _: 2 /* DYNAMIC */
                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["header"]))
                }), 128 /* KEYED_FRAGMENT */))
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["activeIndex"]))
          ])
        ])
      ])
    ])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})